import type { PropsWithChildren } from 'react';
import { Badge, Tooltip } from 'antd';
import type { TooltipProps, BadgeProps } from 'antd';

interface SkyLabelBadgeProps extends PropsWithChildren<{}> {
  tooltip?: string | TooltipProps;
  badgeProps?: BadgeProps;
  copyable?: boolean;
  ellipsis?: boolean;
}

/**
 * Wrapper component that handles badge and tooltip display logic.
 * Supports string or TooltipProps object formats for tooltip configuration.
 */
export const SkyLabelBadge = ({
  children,
  tooltip = undefined,
  badgeProps = {},
  copyable = false,
  ellipsis = false,
}: SkyLabelBadgeProps) => {
  // Merged rather than overwritten by the spread below: a caller's own
  // badgeProps.className must not silently drop the ellipsis sizing contract
  // and reintroduce the resize bug (SKY-3589).
  const { className: badgePropsClassName, ...restBadgeProps } = badgeProps;

  const defaultBadgeProps: BadgeProps = {
    dot: tooltip ? true : false,
    color: 'blue',
    offset: [copyable ? -20 : 0, 0],
    classNames: {
      root: `${tooltip ? 'cursor-help' : ''} text-inherit text-[length:inherit]`,
    },
    // The ellipsis text needs a bounded, shrinkable box to truncate against.
    // `flex-1` claims the width left over by the optional icon in the (default)
    // flex wrapper, `w-full` covers a wrapper whose display was overridden to a
    // non-flex value, and `min-w-0` lets either shrink below its content size.
    className:
      [ellipsis ? 'inline-flex w-full min-w-0 flex-1' : undefined, badgePropsClassName]
        .filter(Boolean)
        .join(' ') || undefined,
    ...restBadgeProps,
  };

  const componentWithBadge = (
    <Badge {...defaultBadgeProps} data-testid="sky-label-badge">
      {children}
    </Badge>
  );

  // No tooltip needed if tooltip is undefined
  if (!tooltip) {
    return <>{componentWithBadge}</>;
  }

  const tooltipOpts: TooltipProps = typeof tooltip === 'string' ? { title: tooltip } : tooltip;

  return (
    <Tooltip {...tooltipOpts} trigger={['click', 'hover']}>
      <Badge {...defaultBadgeProps} data-testid="sky-label-badge">
        {children}
      </Badge>
    </Tooltip>
  );
};
